import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, GripVertical, Search, Sparkles } from 'lucide-react';
import { libraryApi } from '../../lib/api/libraryApi';
import { ADVANCED_INTERVIEW_TYPE_LABELS } from 'src/constants/advancedInterviewTypes';

const DIFFICULTY_COLORS = {
  Easy: 'bg-emerald-100 text-emerald-800',
  Medium: 'bg-amber-100 text-amber-800',
  Hard: 'bg-rose-100 text-rose-800',
  Expert: 'bg-purple-100 text-purple-800',
};

const getDifficultyColor = (difficulty) =>
  DIFFICULTY_COLORS[difficulty] || 'bg-gray-100 text-gray-800';

const defaultProctoringSettings = {
  browserActivityMonitoring: true,
  responseDelayTracking: true,
  speechPatternAnalysis: true,
  transcriptAnalysis: true,
  multiSpeakerDetection: true,
  voiceConsistencyCheck: true,
  randomInterruptQuestions: true,
  facePresenceDetection: true,
  multipleFaceDetection: true,
  faceIdentityConsistency: true,
};

const flowSteps = [
  { id: 1, label: 'Select' },
  { id: 2, label: 'View Questions' },
  { id: 3, label: 'Proctoring' },
];

const AIInterviewLibraryPickerModal = ({ open, onClose, onPick, embedded = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [proctoring, setProctoring] = useState(defaultProctoringSettings);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
  const [expandedIntro, setExpandedIntro] = useState(false);
  const [expandedOutro, setExpandedOutro] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm, open]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDebouncedSearch('');
      setCategoryFilter('');
      setDifficultyFilter('');
      setCurrentPage(1);
      setSelectedInterviewId(null);
      setCurrentStep(1);
      setProctoring(defaultProctoringSettings);
    }
  }, [open]);

  useEffect(() => {
    setExpandedQuestionIndex(null);
    setExpandedIntro(false);
    setExpandedOutro(false);
  }, [selectedInterviewId]);

  const { data, isLoading } = useQuery({
    queryKey: ['library-ai-interviews-stats', 'picker', currentPage, debouncedSearch, categoryFilter, difficultyFilter],
    queryFn: () =>
      libraryApi.getAIInterviewsStats({
        page: currentPage,
        limit: 20,
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        difficulty: difficultyFilter || undefined,
      }),
    enabled: open,
    staleTime: 30 * 1000,
  });

  const interviews = data?.interviews || [];
  const pagination = data?.pagination || { current: 1, total: 1, totalCount: 0 };

  const { data: selectedInterviewDetails, isLoading: loadingSelectedInterview } = useQuery({
    queryKey: ['library-ai-interview', selectedInterviewId, 'details'],
    queryFn: () => libraryApi.getAIInterviewById(selectedInterviewId),
    enabled: open && !!selectedInterviewId,
    staleTime: 2 * 60 * 1000,
  });

  const canPick = useMemo(() => typeof onPick === 'function', [onPick]);
  const selectedInterview = useMemo(
    () => interviews.find((item) => item._id === selectedInterviewId) || null,
    [interviews, selectedInterviewId]
  );
  const selectedQuestions = useMemo(
    () => (Array.isArray(selectedInterviewDetails?.questions) ? selectedInterviewDetails.questions : []),
    [selectedInterviewDetails]
  );
  const selectedRubrics = useMemo(
    () => (Array.isArray(selectedInterviewDetails?.rubrics) ? selectedInterviewDetails.rubrics : []),
    [selectedInterviewDetails]
  );
  const introText = selectedInterviewDetails?.aiIntro || '';
  const outroText = selectedInterviewDetails?.aiOutro || '';
  const isStepAccessible = useMemo(
    () => ({
      1: true,
      2: Boolean(selectedInterviewId),
      3: Boolean(selectedInterviewId),
    }),
    [selectedInterviewId]
  );

  const totalQuestionsToDisplay = useMemo(() => {
    if (selectedInterview?.advancedType === 'rubric-based') {
      return selectedRubrics.reduce((sum, m) => sum + Math.min(m.questionsPerInterview || 1, Array.isArray(m.questionBank) ? m.questionBank.length : 0), 0);
    }
    return selectedQuestions.length;
  }, [selectedInterview, selectedRubrics, selectedQuestions]);

  if (!open) return null;

  return (
    <div
      className={
        embedded
          ? 'flex flex-1 min-h-0'
          : 'fixed inset-0 z-[220] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/50'
      }
      style={embedded ? { minHeight: 0, maxHeight: '100%' } : undefined}
    >
      {!embedded && <div className="fixed inset-0 transition-opacity" onClick={onClose} aria-hidden />}
      <div
        className={
          embedded
            ? 'relative flex flex-col w-full h-full min-h-0 bg-white overflow-hidden'
            : 'relative flex flex-col w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out'
        }
        style={{
          minHeight: embedded ? 0 : '620px',
          height: embedded ? '100%' : undefined,
          maxHeight: embedded ? '100%' : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Choose from AI Interview Library</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {currentStep === 1 && 'Select an AI interview template from your library.'}
              {currentStep === 2 && 'Review questions from the selected template (read-only).'}
              {currentStep === 3 && 'Configure proctoring rules before using this interview.'}
            </p>
          </div>
          {/* <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            ✕
          </button> */}
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          <div className="w-52 flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col">
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {flowSteps.map((step, index) => {
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id;
                const accessible = isStepAccessible[step.id];
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => (accessible ? setCurrentStep(step.id) : null)}
                    disabled={!accessible}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${isActive
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : accessible
                          ? 'text-gray-600 hover:bg-gray-100'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${isActive
                          ? 'bg-[#007BFF] text-white'
                          : isComplete
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {index + 1}
                    </div>
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-[#007BFF]' : 'text-gray-700'}`}>
                      {step.label}
                    </p>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
            {currentStep === 1 && (
              <>
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] hover:border-gray-300 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={categoryFilter}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] text-gray-700"
                      >
                        <option value="">All categories</option>
                        {['Sales', 'Software Development', 'Marketing', 'General', 'IT', 'Engineering', 'Data & Analytics', 'Product Management'].map(
                          (c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          )
                        )}
                      </select>
                      <select
                        value={difficultyFilter}
                        onChange={(e) => {
                          setDifficultyFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] text-gray-700"
                      >
                        <option value="">All difficulties</option>
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                        <option value="Expert">Expert</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
                  style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center py-24">
                      <div className="h-10 w-10 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : interviews.length === 0 ? (
                    <div className="text-center py-16 px-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No AI interviews found</h3>
                      <p className="text-sm text-gray-500">Try adjusting your search or filters.</p>
                    </div>
                  ) : (
                    <div className="px-6 py-4">
                      <div className="border border-gray-200 rounded-xl overflow-hidden flex flex-col">
                        <div className="grid grid-cols-[2.25rem_1fr_4rem_4.5rem_5.5rem] gap-x-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                          <span />
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Title</span>
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Questions</span>
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Duration</span>
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Category</span>
                        </div>

                        {interviews.map((row, idx) => {
                          const isSelected = selectedInterviewId === row._id;
                          let questionCount = row.numberOfQuestions || row.questions?.length || 0;
                          if (row.advancedType === 'rubric-based' && Array.isArray(row.rubrics)) {
                            questionCount = row.rubrics.reduce((sum, m) => sum + Math.min(m.questionsPerInterview || 1, Array.isArray(m.questionBank) ? m.questionBank.length : 0), 0);
                          }
                          const duration = row.estimatedTime || row.duration;
                          const category = row.category;
                          return (
                            <button
                              key={row._id}
                              type="button"
                              data-testid="library-interview-item"
                              onClick={() => setSelectedInterviewId(row._id)}
                              className={`w-full grid grid-cols-[2.25rem_1fr_4rem_4.5rem_5.5rem] gap-x-3 items-center px-4 py-3 text-left transition-all duration-150 ${idx !== 0 ? 'border-t border-gray-100' : ''
                                } ${isSelected ? 'bg-[#2563eb]/5' : 'hover:bg-gray-50'}`}
                            >
                              <div className="flex justify-center">
                                <div
                                  className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#2563eb] bg-[#2563eb]' : 'border-gray-300'
                                    }`}
                                >
                                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                              </div>

                              <div className="min-w-0 overflow-hidden">
                                <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap scrollbar-hidden w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                  <style dangerouslySetInnerHTML={{
                                    __html: `
                                    .scrollbar-hidden::-webkit-scrollbar {
                                      display: none !important;
                                    }
                                  ` }} />
                                  <span className={`text-sm font-medium shrink-0 ${isSelected ? 'text-[#2563eb]' : 'text-gray-900'}`}>
                                    {row.title}
                                  </span>
                                  {(() => {
                                    const LANGUAGE_BADGES = {
                                      hi: { label: 'Hindi', title: 'Conducted in Hindi' },
                                      hindi: { label: 'Hindi', title: 'Conducted in Hindi' },
                                      'hi-en': { label: 'Hinglish', title: 'Conducted in Hinglish' },
                                      'en-hi': { label: 'Hinglish 2', title: 'Conducted in English, Hindi answers welcome' },
                                      kn: { label: 'Kannada', title: 'Conducted in Kannada' },
                                      ta: { label: 'Tamil', title: 'Conducted in Tamil' },
                                      bn: { label: 'Bengali', title: 'Conducted in Bengali' },
                                      te: { label: 'Telugu', title: 'Conducted in Telugu' },
                                      mr: { label: 'Marathi', title: 'Conducted in Marathi' },
                                      gu: { label: 'Gujarati', title: 'Conducted in Gujarati' },
                                      ml: { label: 'Malayalam', title: 'Conducted in Malayalam' },
                                      pa: { label: 'Punjabi', title: 'Conducted in Punjabi' },
                                      tl: { label: 'Taglish', title: 'Conducted in Tagalog' },
                                      fr: { label: 'French', title: 'Conducted in French' },
                                    };
                                    const badge = LANGUAGE_BADGES[row.config?.language];
                                    return badge ? (
                                      <span
                                        className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-800"
                                        title={badge.title}
                                      >
                                        {badge.label}
                                      </span>
                                    ) : null;
                                  })()}
                                  {row.advancedType && (
                                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-100 text-purple-700">
                                      {ADVANCED_INTERVIEW_TYPE_LABELS[row.advancedType] || row.advancedType}
                                    </span>
                                  )}
                                </div>
                                {row.description && (
                                  <div className="overflow-x-auto whitespace-nowrap scrollbar-hidden mt-0.5 w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    <p className="text-[11px] text-gray-400 leading-tight">{row.description}</p>
                                  </div>
                                )}
                              </div>

                              <span className="text-xs text-gray-600 text-center tabular-nums">
                                {questionCount}
                              </span>

                              <span className="text-xs text-gray-600 text-center tabular-nums">
                                {duration ? `${duration}m` : '—'}
                              </span>

                              <span className="text-xs text-gray-500 text-center truncate" title={category || ''}>
                                {category ? category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'General'}
                              </span>

                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {currentStep === 2 && (
              <div
                className="flex-1 min-h-0 overflow-y-auto px-6 py-4 overscroll-contain"
                style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
              >
                {loadingSelectedInterview ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="h-10 w-10 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : totalQuestionsToDisplay === 0 ? (
                  <div className="text-center py-16">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No questions found</h3>
                    <p className="text-sm text-gray-500">The selected template does not contain any questions.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-w-4xl">
                    <div className="pb-1 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Interview Flow Edit - {totalQuestionsToDisplay}{' '}
                          {totalQuestionsToDisplay === 1 ? 'question' : 'questions'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Review-only mode. Questions and expected answers cannot be edited here.
                        </p>
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <Sparkles className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Introduction Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {introText || 'No introduction message saved'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedIntro}
                          aria-label={expandedIntro ? 'Collapse intro' : 'Expand intro'}
                        >
                          {expandedIntro ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedIntro ? 'max-h-[240px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100">
                          <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-700 whitespace-pre-line">
                            {introText || 'No introduction message saved.'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedInterview?.advancedType === 'rubric-based' ? (
                      <div className="space-y-4">
                        {selectedRubrics.map((module, mIdx) => (
                          <div key={module._id || mIdx} className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-800">{module.name || 'Unnamed Module'}</h4>
                                <p className="text-xs text-gray-500 mt-0.5">{module.questionBank?.length || 0} questions in bank</p>
                              </div>
                              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                                Pick {module.questionsPerInterview}
                              </span>
                            </div>
                            <div className="p-4 space-y-3">
                              {module.questionBank?.map((q, qIdx) => (
                                <div key={q._id || qIdx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex gap-3">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                                    {qIdx + 1}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">{q.prompt}</p>
                                    <div className="mt-1.5 flex gap-2">
                                      {q.type === 'yes_no' ? (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">Yes/No</span>
                                      ) : (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">Open-ended</span>
                                      )}
                                      {q.critical && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded border border-red-200">Critical</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      selectedQuestions.map((q, i) => (
                        <div key={`${q.prompt}-${i}`} className="rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-all">
                        <div className="flex items-start gap-2 p-4">
                          <div className="flex-shrink-0 mt-0.5 text-gray-300">
                            <GripVertical className="w-5 h-5" />
                          </div>
                          <div
                            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${q.section === 'eligibility'
                                ? 'bg-yellow-500 text-white'
                                : q.section === 'behavioral'
                                  ? 'bg-green-500 text-white'
                                  : q.section === 'cultural'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-blue-500 text-white'
                              }`}
                          >
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setExpandedQuestionIndex((prev) => (prev === i ? null : i))}
                                className="flex-1 min-w-0 text-left"
                              >
                                <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                  {q.prompt || 'No question text'}
                                </p>
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${q.section === 'eligibility'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : q.section === 'behavioral'
                                          ? 'bg-green-100 text-green-700'
                                          : q.section === 'cultural'
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-blue-100 text-blue-700'
                                      }`}
                                  >
                                    {q.section === 'eligibility'
                                      ? 'Eligibility'
                                      : q.section === 'behavioral'
                                        ? 'Behavioral'
                                        : q.section === 'cultural'
                                          ? 'Cultural'
                                          : 'Technical'}
                                  </span>
                                  {q.type === 'yes_no' && q.critical && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 border border-red-200">
                                      Critical
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-500">
                                    {q.type === 'yes_no' ? 'Yes/No' : 'Detailed'}
                                  </span>
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={() => setExpandedQuestionIndex((prev) => (prev === i ? null : i))}
                                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                aria-expanded={expandedQuestionIndex === i}
                                aria-label={expandedQuestionIndex === i ? 'Collapse question' : 'Expand question'}
                              >
                                {expandedQuestionIndex === i ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            <div
                              className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedQuestionIndex === i ? 'max-h-[560px]' : 'max-h-0'
                                }`}
                            >
                              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                                <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                                  {q.prompt}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-600">Type:</span>
                                  <span className="text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                                    {q.type === 'yes_no' ? 'Yes/No' : 'Detailed Answer'}
                                  </span>
                                </div>

                                {q.type === 'yes_no' && q.expectedAnswer && (
                                  <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100">
                                    <label className="block text-xs font-medium text-indigo-700 mb-2">
                                      Expected Answer
                                    </label>
                                    <ul className="space-y-1.5">
                                      <li className="flex items-start gap-2 text-xs text-gray-800">
                                        <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
                                          1
                                        </span>
                                        <span className="text-sm font-medium text-gray-700">{q.expectedAnswer}</span>
                                      </li>
                                    </ul>
                                  </div>
                                )}

                                {q.type !== 'yes_no' && (
                                  <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100 space-y-2">
                                    <label className="block text-xs font-medium text-indigo-700">
                                      Expected Answer Points (for scoring)
                                    </label>
                                    {Array.isArray(q.expectedPoints) && q.expectedPoints.length > 0 ? (
                                      <div className="space-y-2">
                                        {q.expectedPoints.map((point, pIdx) => (
                                          <div key={`${point}-${pIdx}`} className="flex items-start gap-2">
                                            <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
                                              {pIdx + 1}
                                            </span>
                                            <p className="text-sm text-gray-700">{point}</p>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-600">No expected points added.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )))}

                    {/* Closing Message */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <Sparkles className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Closing Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {outroText || 'No closing message saved'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedOutro}
                          aria-label={expandedOutro ? 'Collapse outro' : 'Expand outro'}
                        >
                          {expandedOutro ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedOutro ? 'max-h-[240px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100">
                          <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-700 whitespace-pre-line">
                            {outroText || 'No closing message saved.'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div
                className="flex-1 min-h-0 overflow-y-auto px-6 py-4 overscroll-contain"
                style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
              >
                <div className="space-y-4 max-w-3xl">
                  {[
                    {
                      key: 'browserActivityMonitoring',
                      title: 'Browser Activity Monitoring',
                      desc: 'Track tab and window switches during the interview.',
                    },
                    {
                      key: 'responseDelayTracking',
                      title: 'Response Delay Tracking',
                      desc: 'Monitor long pauses between question and response.',
                    },
                    {
                      key: 'speechPatternAnalysis',
                      title: 'Speech Pattern Analysis',
                      desc: 'Analyze answer delivery for suspicious speaking patterns.',
                    },
                    {
                      key: 'transcriptAnalysis',
                      title: 'Transcript Analysis',
                      desc: 'Check transcript consistency and suspicious patterns.',
                    },
                    {
                      key: 'multiSpeakerDetection',
                      title: 'Multi-Speaker Detection',
                      desc: 'Detect if multiple speakers are present in audio.',
                    },
                    {
                      key: 'voiceConsistencyCheck',
                      title: 'Voice Consistency Check',
                      desc: 'Verify the same voice throughout the interview.',
                    },
                    {
                      key: 'randomInterruptQuestions',
                      title: 'Random Interrupt Questions',
                      desc: 'Inject random prompts to verify candidate presence.',
                    },
                  ].map((item) => (
                    <label
                      key={item.key}
                      className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 block">{item.title}</span>
                        <span className="text-xs text-gray-500 mt-1 block">{item.desc}</span>
                      </div>
                      <span
                        className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 transition-colors ${proctoring[item.key] ? 'bg-[#007BFF] justify-end' : 'bg-gray-200 justify-start'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(proctoring[item.key])}
                          onChange={(e) =>
                            setProctoring((prev) => ({
                              ...prev,
                              [item.key]: e.target.checked,
                            }))
                          }
                          className="sr-only"
                        />
                        <span className="h-5 w-5 rounded-full bg-white shadow" />
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 px-6 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-3">
            {/* {currentStep === 1 && (
              <>
                <span className="text-sm text-gray-600">
                  Page {pagination.current} of {pagination.total}
                </span>
                {pagination.total > 1 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={pagination.current <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={pagination.current >= pagination.total}
                      onClick={() => setCurrentPage((p) => Math.min(pagination.total, p + 1))}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )} */}

            {currentStep === 1 && (
              <button
                type="button"
                disabled={!selectedInterviewId}
                onClick={() => setCurrentStep(2)}
                className="px-5 py-2.5 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}
            {currentStep === 2 && (
              <button
                type="button"
                disabled={loadingSelectedInterview || !selectedInterviewId}
                onClick={() => setCurrentStep(3)}
                className="px-5 py-2.5 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Proctoring
              </button>
            )}
            {currentStep === 3 && (
              <button
                type="button"
                disabled={!canPick || !selectedInterviewId}
                onClick={() => onPick(selectedInterviewId, { proctoring })}
                className="px-5 py-2.5 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Interview
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInterviewLibraryPickerModal;

